import { ArrowUpRight, ScanLine, FileText, Eraser } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";
import { IMAGES, formatEur } from "@/lib/site";

const READOUT = [
  ["P0420", "KATALYSATORSYSTEM · SCHWELLE UNTERSCHRITTEN", "GEPRUEFT"],
  ["P0301", "ZYLINDER 1 · MISCHAUSBRENNER ERKANNT", "GEPRUEFT"],
  ["P0171", "GEMISCHZUSAMMENSETZUNG · ZU MAGER", "PRUEFEN"],
];

export default function ObdSection({ onQuote }) {
  const c = useContent();
  const obd = c.addons.find((a) => a.id === "obd" && a.active !== false);
  if (!obd) return null;

  return (
    <section id="fehlerspeicher" data-testid="obd-highlight-section" className="relative py-16 sm:py-20 border-t border-white/5 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <span className="inline-flex items-center rounded-full bg-rose-500/10 border border-rose-500/30 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-rose-400">
            Zusatzleistung
          </span>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">
            Motorkontrollleuchte leuchtet? <span className="text-rose-400">Wir schauen nach.</span>
          </h2>
          <p className="mt-3 max-w-lg text-base text-zinc-300 leading-relaxed">
            Ich lese den Fehlerspeicher deines Fahrzeugs vor Ort aus, erkläre dir die Fehlercodes
            verständlich und händige dir einen Bericht aus. Auf Wunsch lösche ich danach sporadische
            Fehler.
          </p>
          <p className="mt-3 max-w-lg text-sm text-zinc-400 leading-relaxed">
            Wichtig: Das ist ein Diagnose-Service für {formatEur(obd.price)} – die Behebung der
            Ursache ist nicht enthalten. Bleibt die Leuchte dauerhaft an, empfehle ich dir den Weg
            in die Werkstatt; du weißt danach aber genau, woran es liegt.
          </p>

          <div className="mt-6 flex flex-col gap-3">
            {[
              { icon: ScanLine, title: "Auslesen", text: "OBD-Gerät ans Fahrzeug – Codes und Live-Daten werden ausgelesen." },
              { icon: FileText, title: "Bericht", text: "Fehlercodes verständlich erklärt, Bericht zum Mitnehmen." },
              { icon: Eraser, title: "Löschen (nach Absprache)", text: "Sporadische Fehler werden gelöscht – die Ursache bleibt ggf. bestehen." },
            ].map((s) => (
              <div key={s.title} className="flex gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-rose-500/25 bg-rose-500/5">
                  <s.icon className="h-4 w-4 text-rose-400" />
                </span>
                <div>
                  <p className="font-semibold text-zinc-100 text-sm">{s.title}</p>
                  <p className="mt-0.5 text-sm text-zinc-400 leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            data-testid="obd-cta-button"
            onClick={() => onQuote({ service: obd.name, notes: `OBD-Diagnose (${formatEur(obd.price)})` })}
            className="group mt-7 inline-flex h-11 items-center gap-2 rounded-full border border-rose-500/50 px-6 text-sm font-semibold text-rose-300 hover:bg-rose-500/10 transition-colors"
          >
            Diagnose anfragen
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-[#27272A]">
              <img
                src={IMAGES.cockpit}
                alt="Armaturenbrett mit Motorkontrollleuchte"
                loading="lazy"
                decoding="async"
                className="h-48 w-full object-cover"
              />
            </div>
            <div
              data-testid="obd-terminal"
              className="relative -mt-8 mx-4 rounded-2xl border border-[#27272A] bg-[#0A0A0C] p-5 font-mono text-xs leading-6 shadow-2xl"
            >
              <div className="flex items-center gap-2 border-b border-[#27272A] pb-3 mb-3">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                <span className="ml-2 text-zinc-400">OBD-II DIAGNOSE · VOR ORT</span>
              </div>
              <p className="text-zinc-400">{">"} Fehlerspeicher auslesen …</p>
              {READOUT.map(([code, desc, status]) => (
                <p key={code} className="mt-1">
                  <span className="text-rose-400 font-bold">{code}</span>{" "}
                  <span className="text-zinc-400">{desc}</span>{" "}
                  <span className={status === "PRUEFEN" ? "text-amber-500" : "text-emerald-500"}>[{status}]</span>
                </p>
              ))}
              <p className="mt-2 text-emerald-500">{">"} Bericht erstellt – Erklärung inklusive <span className="blink text-zinc-400">▊</span></p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
